import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useContracts } from '../hooks/useContract';
import { PACKAGES } from '../config/contracts';

export default function Team({ data }) {
  const { evittaRead } = useContracts();
  const { userId, referrals } = data;
  const [pkg, setPkg] = useState(1);
  const [levels, setLevels] = useState(null);
  const [teamSize, setTeamSize] = useState(0);

  const [openLevel, setOpenLevel] = useState(null);
  const [members, setMembers] = useState({}); // key `${pkg}-${level}` -> id[]

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // level counts + team size
  useEffect(() => {
    if (!evittaRead || !userId) return undefined;
    let cancelled = false;
    setOpenLevel(null);
    (async () => {
      const [counts, det] = await Promise.all([
        evittaRead.getMatrixLevelCounts(userId, pkg).catch(() => null),
        evittaRead.getMatrixDetails(userId, pkg).catch(() => null),
      ]);
      if (cancelled || !mounted.current) return;
      setLevels(counts ? counts.map((c) => Number(c)) : null);
      setTeamSize(det ? Number(det.totalTeamSize) : 0);
    })();
    return () => { cancelled = true; };
  }, [evittaRead, userId, pkg]);

  // fetch members for a level on demand
  const loadMembers = useCallback(
    async (level) => {
      const key = `${pkg}-${level}`;
      if (members[key]) return;
      const list = await evittaRead.getMatrixLevelUsers(userId, pkg, level).catch(() => []);
      if (mounted.current) {
        setMembers((m) => ({ ...m, [key]: list.map((id) => Number(id)) }));
      }
    },
    [evittaRead, userId, pkg, members]
  );

  const toggle = (level) => {
    if (openLevel === level) {
      setOpenLevel(null);
    } else {
      setOpenLevel(level);
      loadMembers(level);
    }
  };

  return (
    <div className="tab fade-in">
      {/* Direct team */}
      <div className="section-title"><span className="bar" />My Direct Team</div>
      <div className="card">
        {referrals && referrals.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {referrals.map((id) => (
              <span key={String(id)} className="pill gold">#{String(id)}</span>
            ))}
          </div>
        ) : (
          <div className="empty">No direct referrals yet. Share your link to grow your team!</div>
        )}
      </div>

      {/* Package selector */}
      <div className="section-title"><span className="bar" />Matrix Levels</div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-row">
          <span className="muted" style={{ fontSize: 13 }}>Team in package {pkg}</span>
          <span className="stat-value gold" style={{ fontSize: 18 }}>{teamSize}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {PACKAGES.map((p) => (
            <button
              key={p.id}
              className={`pill ${pkg === p.id ? 'gold' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setPkg(p.id)}
            >
              P{p.id}
            </button>
          ))}
        </div>
      </div>

      {/* Levels accordion */}
      {levels ? (
        <div>
          {levels.map((count, i) => {
            const level = i + 1;
            const cap = Math.pow(2, level);
            const open = openLevel === level;
            const key = `${pkg}-${level}`;
            const list = members[key];
            return (
              <div key={level} className={`lvl ${open ? 'open' : ''}`}>
                <div className="lvl-head" onClick={() => toggle(level)}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="chev">▶</span>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>Level {level}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="pill gold">{count}</span>
                    <span className="dim" style={{ fontSize: 11 }}>/ {cap}</span>
                  </span>
                </div>
                {open && (
                  <div className="lvl-body">
                    {list == null ? (
                      <span className="dim" style={{ fontSize: 12 }}>Loading members…</span>
                    ) : list.length ? (
                      list.map((id) => (
                        <span key={id} className="pill">#{id}</span>
                      ))
                    ) : (
                      <span className="dim" style={{ fontSize: 12 }}>No members at this level yet.</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card"><div className="empty">Loading matrix…</div></div>
      )}
    </div>
  );
}
